import { useGetConnectionMembers } from 'gcs-common/clients/api/entities/connection-member/connection-member-api';
import {
  useCreateETSTicketMutation,
  usePatchETSTicketStatusMutation,
} from 'gcs-common/clients/api/entities/tickets/tickets-api';
import { useUploadETSAttachments } from 'gcs-common/clients/api/entities/tickets/tickets-api-helpers';
import AttachmentsPreview from 'gcs-common/components/AttachmentsPreview/AttachmentsPreview';
import EtsMessageInput from 'gcs-common/components/EtsMessageInput/EtsMessageInput';
import { CHAT_ROUTES } from 'gcs-common/constants/routes';
import { toast } from 'gcs-common/design-system/hooks/use-toast';
import { FormControl, FormField, FormItem } from 'gcs-common/design-system/ui/form';
import { Input } from 'gcs-common/design-system/ui/input';
import { useAttachmentsInput } from 'gcs-common/hooks/useAttachmentsInput';
import { getCurrentUserId } from 'gcs-common/slices/currentUser/currentUserSelectors';
import { getSelectedHouseId } from 'gcs-common/slices/houses/housesSelectors';
import { TICKET_INPUT_CHANNELS } from 'gcs-common/slices/tickets/ticketsConstants';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import type { TicketCreationFormValues } from '../TicketNew/TicketNew';

const TicketCreationForm = () => {
  const form = useFormContext<TicketCreationFormValues>();
  const createETSTicket = useCreateETSTicketMutation();
  const uploadETSAttachments = useUploadETSAttachments();
  const patchETSTicketStatus = usePatchETSTicketStatusMutation();
  const navigate = useNavigate();
  const houseId: string = useSelector(getSelectedHouseId);
  const userId: string = useSelector(getCurrentUserId);

  const { data: connectionMember } = useGetConnectionMembers({
    userId,
    houseId,
  });
  const connectionId = connectionMember ? (connectionMember[0]?.connection?.id ?? '') : '';

  const onSubmit = async (data: TicketCreationFormValues) => {
    const title: string = data.title ? data.title : data.comment?.trim().split('\n')[0]?.slice(0, 256) || '';

    try {
      const result = await createETSTicket.mutateAsync({
        body: {
          title,
          description: data.comment.trim(),
          customerOrderText: undefined,
          erpCustomerId: connectionId,
          inputChannel: TICKET_INPUT_CHANNELS.craftsman.key,
        },
      });

      if (result?.data) {
        if (data.attachments?.length) {
          await uploadETSAttachments.uploadFiles(result.data.id, data.attachments);
        }
        await patchETSTicketStatus.mutateAsync({
          path: {
            ticket_id: result.data.id,
          },
        });

        navigate(CHAT_ROUTES.TICKET(result.data.id));
      }
      form.reset();
    } catch (_error) {
      toast({
        description: 'Fehler bei der Ticketerstellung.',
        duration: 5000,
        variant: 'destructive',
      });
    }
  };

  const { handleFileChange } = useAttachmentsInput(form);

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="flex h-full flex-col">
      <AttachmentsPreview handleFileChange={handleFileChange} />
      <FormField
        control={form.control}
        name="title"
        render={({ field }) => (
          <FormItem className="mt-4 px-4">
            <FormControl>
              <div className="flex flex-col gap-3 rounded-3xl bg-neutral-3 px-4 pt-3 pb-4">
                <div>
                  <span className="font-medium text-base">Eigener Titel (optional)</span>
                  <br />
                  <span className="text-neutral-alpha-9 text-sm">
                    Hilft dir, die Anfrage leichter wiederzufinden. Wird nicht an das Ersatzteil-Team übermittelt.
                  </span>
                </div>

                <Input
                  {...field}
                  id="automated-qa-ticket-title-input"
                  type="text"
                  inputSize="l"
                  className="w-full rounded-xl indent-4 placeholder-neutral-alpha-9"
                  placeholder="z.B. Kommission Müller"
                />
              </div>
            </FormControl>
          </FormItem>
        )}
      />
      <EtsMessageInput
        handleFileChange={handleFileChange}
        isSubmitting={createETSTicket.isPending || uploadETSAttachments.isPending || patchETSTicketStatus.isPending}
      />
    </form>
  );
};

export default TicketCreationForm;
